import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Users, CheckCircle, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { mockData } from '../mock';

const Projects = () => {
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const filteredProjects = filter === 'all' 
    ? mockData.projects 
    : mockData.projects.filter(p => p.status === filter);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Our Projects
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Explore our medical missions across the globe, bringing healthcare to communities in need
          </p>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Filter Tabs */}
          <Tabs defaultValue="all" className="mb-12">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-3">
              <TabsTrigger value="all" onClick={() => setFilter('all')}>All Missions</TabsTrigger>
              <TabsTrigger value="completed" onClick={() => setFilter('completed')}>Completed</TabsTrigger>
              <TabsTrigger value="upcoming" onClick={() => setFilter('upcoming')}>Upcoming</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredProjects.map((project) => {
              const isUpcoming = project.status === 'upcoming';
              return (
              <Card 
                key={project.id} 
                className="overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 border-none shadow-lg cursor-pointer"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                {/* Project Image */}
                <div className="h-64 relative overflow-hidden">
                  <img 
                    src={project.thumbnail}
                    alt={project.title}
                    className={`w-full h-full object-cover ${isUpcoming ? 'blur-sm opacity-40' : ''}`}
                  />
                  {isUpcoming && (
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-600/50 to-blue-600/80"></div>
                  )}
                  <Badge 
                    className={`absolute top-4 right-4 ${
                      project.status === 'completed' 
                        ? 'bg-green-500' 
                        : 'bg-orange-500'
                    } text-white`}
                  >
                    {project.status === 'completed' ? (
                      <><CheckCircle className="h-3 w-3 mr-1" /> Completed</>
                    ) : (
                      <><Clock className="h-3 w-3 mr-1" /> Upcoming</>
                    )}
                  </Badge>
                </div>

                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-600 uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {project.location}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span className="text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        {project.date}
                      </span>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {project.title}
                  </h3>

                  <p className="text-gray-600 mb-6 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {project.description}
                  </p>

                  <div className="flex items-center space-x-2 text-sm font-semibold text-green-600">
                    <Users className="h-4 w-4" />
                    <span>{project.impact}</span>
                  </div>
                </CardContent>
              </Card>
            );
            })}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                No projects found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Impact Banner */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Every Mission Makes a Difference
          </h2>
          <p className="text-xl text-blue-100 mb-8" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            With your support, we can reach more communities and save more lives. Join us in our mission to provide healthcare to those who need it most.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div>
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>650+</div>
              <div className="text-blue-100" style={{ fontFamily: 'Open Sans, sans-serif' }}>Patients Treated</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>5</div>
              <div className="text-blue-100" style={{ fontFamily: 'Open Sans, sans-serif' }}>Missions Completed</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>2</div>
              <div className="text-blue-100" style={{ fontFamily: 'Open Sans, sans-serif' }}>Countries Served</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;